import { useMemo, useState } from "react";
import axios from "axios";
import { Beer, Martini, Minus, Plus, Camera, CheckCircle2, UtensilsCrossed, Loader2 } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BarcodeCamera } from "@/components/recharge/BarcodeCamera";
import { RechargePay } from "@/components/recharge/RechargePay";
import { useGuestLang, LangToggle } from "@/lib/guestI18n";

const DRINKS = [{ key: "BEER", label: "Beer", Icon: Beer }, { key: "COCKTAIL", label: "Cocktail", Icon: Martini }];
const card = "bg-[#131D33] border border-slate-800 rounded-2xl p-5 sm:p-6";

function Balance({ ticket, t }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-[#0B1120] border border-slate-700 p-4" data-testid="recharge-ticket-info">
      <div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("Ticket")}</div><div className="font-mono-fb text-xl text-orange-300" data-testid="recharge-ticket-number">{ticket.ticket_id}</div></div>
      <div className="text-right"><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("Included drinks")}</div><div className="font-display text-3xl font-black text-white" data-testid="recharge-balance">{ticket.drinks_remaining}</div>
        <div className="text-xs text-orange-300" data-testid="recharge-credit">{t("Credit")}: ${Number(ticket.credit || 0).toFixed(2)}</div></div>
    </div>
  );
}

function VerifyStep({ onVerified, t }) {
  const [number, setNumber] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [manual, setManual] = useState(false);

  const submit = async (barcode) => {
    setScanning(false); setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/public/recharge/verify`, barcode ? { barcode } : { ticket_number: number.trim() });
      onVerified(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`${card} space-y-4`} data-testid="recharge-verify-step">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">{t("Scan the barcode on your ticket")}</div>
        {scanning ? (
          <BarcodeCamera onResult={submit} onError={() => { setScanning(false); setError("Camera not available. Please allow camera access or visit Registration."); }} />
        ) : (
          <Button disabled={busy} onClick={() => setScanning(true)} className="w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="recharge-scan-btn">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <><Camera size={18} className="mr-2" /> {t("Scan barcode")}</>}
          </Button>
        )}
        {scanning && <Button variant="ghost" onClick={() => setScanning(false)} className="w-full mt-2 text-slate-400" data-testid="recharge-stop-scan">{t("Stop camera")}</Button>}
      </div>
      {!manual ? (
        <Button variant="outline" onClick={() => { setManual(true); setScanning(false); }} className="w-full h-12 bg-slate-900 border-slate-700 text-slate-200" data-testid="recharge-manual-btn">{t("Enter Ticket Number Manually")}</Button>
      ) : (
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold">{t("Ticket number")}</label>
          <div className="mt-2 flex gap-2">
            <Input autoFocus value={number} onChange={(e) => setNumber(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && number.trim().length >= 3 && submit(null)} placeholder={t("e.g. REG-02K4M9")} className="h-12 text-lg font-mono-fb bg-[#0B1120] border-slate-700 text-white" data-testid="recharge-ticket-input" />
            <Button disabled={number.trim().length < 3 || busy} onClick={() => submit(null)} className="h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="recharge-manual-submit">{t("Continue")}</Button>
          </div>
        </div>
      )}
      {error && <div className="text-sm text-rose-300" data-testid="recharge-verify-error">{t(error)}</div>}
    </div>
  );
}

function Stepper({ d, qty, setQty, price, canAdd, t }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-[#0B1120] border border-slate-700 p-3">
      <div className="flex items-center gap-3"><d.Icon className="text-amber-400" size={22} /><div><div className="font-bold text-white">{t(d.label)}</div><div className="text-xs text-slate-400">${price} {t("each")}</div></div></div>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => setQty(Math.max(0, qty - 1))} className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center" data-testid={`recharge-minus-${d.key}`}><Minus size={16} /></button>
        <span className="w-6 text-center font-display text-2xl font-black" data-testid={`recharge-qty-${d.key}`}>{qty}</span>
        <button type="button" disabled={!canAdd} onClick={() => setQty(qty + 1)} className="w-10 h-10 rounded-full bg-orange-500 disabled:opacity-40 flex items-center justify-center" data-testid={`recharge-plus-${d.key}`}><Plus size={16} /></button>
      </div>
    </div>
  );
}

function Confirmation({ result, onAgain, t }) {
  const cell = (label, value, tid, cls = "font-bold") => (
    <div className="rounded-xl bg-[#0B1120] p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">{t(label)}</div><div className={cls} data-testid={tid}>{value}</div></div>
  );
  return (
    <div className={`${card} text-center space-y-4 border-emerald-500/40`} data-testid="recharge-confirmation">
      <CheckCircle2 className="mx-auto text-emerald-400" size={48} />
      <div className="font-display text-3xl font-black">{t("PAYMENT CONFIRMED")}</div>
      <div className="grid grid-cols-2 gap-3 text-left">
        {cell("Ticket", result.ticket.ticket_id, "recharge-confirm-ticket", "font-mono-fb text-orange-300")}
        {cell("Credit added", `$${result.total_amount.toFixed(2)}`, "recharge-confirm-qty")}
        {cell("Amount paid", `$${result.total_amount.toFixed(2)}`, "recharge-confirm-amount")}
        {cell("Credit balance", `$${Number(result.ticket.credit || 0).toFixed(2)}`, "recharge-confirm-balance", "font-bold text-emerald-300")}
      </div>
      <Button onClick={onAgain} className="w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="recharge-again-btn">{t("Buy more drinks")}</Button>
    </div>
  );
}

function BuyStep({ session, onPaid, t, lang }) {
  const client = useMemo(() => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${session.session}` } }), [session.session]);
  const [qty, setQty] = useState({ BEER: 0, COCKTAIL: 0 });
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const count = qty.BEER + qty.COCKTAIL;
  const total = DRINKS.reduce((s, d) => s + qty[d.key] * session.prices[d.key], 0);

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await client.post("/public/recharge/create-intent", { items: DRINKS.map((d) => ({ drink_type: d.key, quantity: qty[d.key] })) });
      setPending(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    try {
      const { data } = await client.post(`/public/recharge/cancel/${pending.reference}`);
      if (data.status === "paid") {
        const { data: s } = await client.get(`/public/recharge/status/${pending.reference}`);
        onPaid(s); return;
      }
    } catch (e) { /* noop */ }
    setPending(null);
  };

  return (
    <div className={`${card} space-y-4`} data-testid="recharge-buy-step">
      <Balance ticket={session.ticket} t={t} />
      {pending ? (
        <RechargePay pending={pending} client={client} onPaid={onPaid} onCancel={cancel} t={t} lang={lang} />
      ) : (
        <>
          {DRINKS.map((d) => <Stepper key={d.key} d={d} t={t} qty={qty[d.key]} price={session.prices[d.key]} canAdd={count < session.max_drinks} setQty={(v) => setQty({ ...qty, [d.key]: v })} />)}
          <div className="flex justify-between items-end"><span className="text-slate-400 text-sm">{count} {t("drink(s)")} · {t("max")} {session.max_drinks}</span><span className="font-display text-3xl font-black" data-testid="recharge-total">${total.toFixed(2)}</span></div>
          {error && <div className="text-sm text-rose-300" data-testid="recharge-buy-error">{t(error)}</div>}
          <Button disabled={!count || busy || !session.stripe_enabled} onClick={start} className="w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="recharge-continue-btn">
            {busy ? <Loader2 className="animate-spin" size={18} /> : t("Continue to payment")}
          </Button>
        </>
      )}
    </div>
  );
}

export default function GuestRecharge() {
  const { lang, setLang, t } = useGuestLang();
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const again = () => { setSession({ ...session, ticket: result.ticket }); setResult(null); };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 relative overflow-hidden" data-testid="guest-recharge-page">
      <LangToggle lang={lang} setLang={setLang} />
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-orange-500/20 blur-3xl" />
      <div className="relative z-10 max-w-md mx-auto px-4 pt-14 pb-8 space-y-6">
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center mb-3"><UtensilsCrossed className="text-white" size={26} /></div>
          <h1 className="font-display text-4xl font-black tracking-tight">FOOD BAZAAR</h1>
          <h2 className="font-display text-base md:text-lg font-bold text-orange-400 tracking-tight">{t("RECHARGE YOUR DRINKS")}</h2>
        </div>
        {result ? <Confirmation result={result} onAgain={again} t={t} />
          : session ? <BuyStep key={`${session.ticket.drinks_remaining}-${session.ticket.credit}`} session={session} onPaid={setResult} t={t} lang={lang} />
          : <VerifyStep onVerified={setSession} t={t} />}
        <p className="text-center text-xs text-slate-500">{t("Paying cash? Visit the Registration desk.")}</p>
        <a href="/event" className="block text-center text-xs text-orange-400 hover:underline" data-testid="recharge-back-portal">{t("← Back to guest portal")}</a>
      </div>
    </div>
  );
}
